import React, { useCallback, useMemo, useState } from "react";
import { FormField } from "@/shared/components/FormField";
import DynamicOptionsField from "@/shared/components/DynamicOptionsField";
import { PartialPet, Pet, SimpleOption } from "@/shared/types/Quote.interface";
import Strings from "@/shared/utils/Strings.constants";
import { CoverageUtils, CoverageValue } from "@/shared/utils/CoverageUtils";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { DefaultPlanConfig } from "@/shared/hooks/useDefaultPlans";
import { cn } from "@/shared/utils";
import ForbesLogo from "@/shared/media/images/logo-forbes.svg";
import { PublicConfig } from "@/shared/PublicConfig";
import { useStatsig } from "@/shared/hooks/useStatsig";
import { AnalyticsUtils } from "@/shared/utils/AnalyticsUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBadgeDollar } from "@fortawesome/pro-solid-svg-icons/faBadgeDollar";
import { faShieldCheck } from "@fortawesome/pro-solid-svg-icons/faShieldCheck";
import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons/faArrowLeft";

export interface ControlSettings {
    annualLimitValue?: string;
    reimbursementValue?: string;
    annualDeductibleValue?: string;
    activePet?: PartialPet;
    allPets?: PartialPet[];
    floatingVariant?: "default" | "floating" | "floating-inner" | "icon";
    isCustomControlsEnabled?: boolean;
    isMultiPetLinked?: boolean;
    activePetCoverage?: CoverageValue;
    defaultPlanConfig?: DefaultPlanConfig;
    isRecommendedPlansEnabled?: boolean;
}

export interface CoverageHandlers {
    onAnnualLimitChange: (value: string) => void;
    onReimbursementChange: (value: string) => void;
    onDeductibleChange: (value: string) => void;
}

interface CoverageParameterControlsProps {
    settings: ControlSettings;
    handlers: CoverageHandlers;
    showReimbursementTip?: boolean;
    isTermsDefinitionsEnabled?: boolean;
    view?: "mobile" | "desktop" | "na";
}

export type ReimbursementTipVariant = "affordance" | "descriptor";

export const CoverageParameterControls: React.FC<CoverageParameterControlsProps> = props => {
    const {
        settings: { annualLimitValue, reimbursementValue, annualDeductibleValue, activePet, floatingVariant },
        handlers: { onAnnualLimitChange, onReimbursementChange, onDeductibleChange },
        showReimbursementTip,
        isTermsDefinitionsEnabled = false,
        view = "na"
    } = props;

    const { appState } = useAppLayerContext();
    const { isAnnualBilling, petPlansObject, highlightAnnualLimitForbes, quoteQuery, showButtonStyle } = appState;

    const { showReimbursementSuggestion } = useStatsig({
        layerId: PublicConfig.STATSIG_LAYER_ID_MAIN,
        layerParameters: [{ key: "showReimbursementSuggestion", defaultValue: "" }] as const,
        queryParams: AnalyticsUtils.getQueryParams(quoteQuery?.data?.extra?.queryParams)
    });

    const [touchedHints, setTouchedHints] = useState({ annualLimit: false, reimbursement: false, deductible: false });

    const handleAnnualLimitChange = useCallback(
        (value: string) => {
            if (isTermsDefinitionsEnabled) setTouchedHints(t => ({ ...t, annualLimit: true }));
            onAnnualLimitChange(value);
        },
        [isTermsDefinitionsEnabled, onAnnualLimitChange]
    );
    const handleReimbursementChange = useCallback(
        (value: string) => {
            if (isTermsDefinitionsEnabled) setTouchedHints(t => ({ ...t, reimbursement: true }));
            onReimbursementChange(value);
        },
        [isTermsDefinitionsEnabled, onReimbursementChange]
    );
    const handleDeductibleChange = useCallback(
        (value: string) => {
            if (isTermsDefinitionsEnabled) setTouchedHints(t => ({ ...t, deductible: true }));
            onDeductibleChange(value);
        },
        [isTermsDefinitionsEnabled, onDeductibleChange]
    );

    const renderTriggerHint = (hintText: string) => <p className="mt-2 text-xs font-medium text-content-brand-b">{hintText}</p>;

    const { petPlans } = petPlansObject || {};

    const activePetAllPlans = useMemo(() => petPlans?.[activePet?.id ?? ``] ?? [], [activePet?.id, petPlans]);

    const allCoverageOptions = useMemo(
        () => (!!activePetAllPlans.length ? CoverageUtils.formatCustomCoverageData(activePetAllPlans, isAnnualBilling) : null),
        [activePetAllPlans, isAnnualBilling]
    );

    const annualLimitWrapperClasses = {
        "bg-background-brand-b-secondary rounded-xl p-2 -mx-2 -mt-2 sm:mt-auto sm:-mx-1": highlightAnnualLimitForbes
    };

    const radioGroupItemClasses = {
        "data-[state=on]:bg-background-brand-b-primary bg-background-primary shadow-sm": highlightAnnualLimitForbes
    };
    //

    const { annualLimits, annualDeductibles, reimbursementRates } = allCoverageOptions || {};

    // A/B test (showButtonStyle): shorten the "$1000" deductible label to "$1k".
    const deductibleOptions = useMemo(
        () => (showButtonStyle ? annualDeductibles?.map(option => (option.label === "$1000" ? { ...option, label: "$1k" } : option)) : annualDeductibles),
        [annualDeductibles, showButtonStyle]
    );

    const updatedAnnualLimits = useMemo(() => {
        if (!annualLimits) {
            return [];
        }

        const abbreviatedLimits = annualLimits.map(option => {
            // Special case: keep '$2,500' and 'Unlimited' unchanged
            if (option.value === "2500" || option.value === "-1") {
                return option;
            }

            const amount = parseInt(option.value, 10);
            if (isNaN(amount)) {
                return option;
            }

            const abbreviatedLabel = new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
                notation: "compact",
                maximumFractionDigits: 0
            }).format(amount); // Formats 5000 to "$5K"

            return { ...option, label: abbreviatedLabel };
        });

        return abbreviatedLimits;
    }, [annualLimits]);

    const updateCoverageOptionsWithTips = useCallback((options: SimpleOption[], optionsKey: "reimbursement" | "annualLimit" | "deductible", activePet: Pet) => {
        if (!!options?.length && !!activePet?.species && activePet?.age !== undefined && activePet?.age !== null) {
            const recommendedMap = {
                reimbursement: {
                    "80": Strings.MOST_POPULAR,
                    "90": Strings.MOST_CASHBACK
                }
            };
            return options.map(option => {
                // Check if this option matches our recommendation
                const isRecommended = optionsKey === "reimbursement" && (Number(option.value) === 80 || Number(option.value) === 90);
                let tipText = "";

                // Add recommendation indicator if this is the recommended option
                if (isRecommended) {
                    tipText = `${recommendedMap[optionsKey][option.value as keyof (typeof recommendedMap)["reimbursement"]]}`;
                }

                return {
                    value: option.value,
                    label: option.label,
                    tipText
                };
            });
        }
        return options;
    }, []);

    const updatedReimbursementRates = useMemo(() => {
        return updateCoverageOptionsWithTips(reimbursementRates ?? [], "reimbursement", activePet as Pet);
    }, [updateCoverageOptionsWithTips, reimbursementRates, activePet]);

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3">
                {updatedAnnualLimits && (
                    <FormField className={cn(annualLimitWrapperClasses)}>
                        <DynamicOptionsField
                            variant={floatingVariant}
                            id="coverage-annual-limit"
                            options={updatedAnnualLimits}
                            label={Strings.ANNUAL_LIMIT}
                            initialValue={annualLimitValue}
                            constrainWidthToTrigger={true}
                            customControlType={"buttons"}
                            onValueChange={handleAnnualLimitChange}
                            radioGroupItemClassName={cn(radioGroupItemClasses)}
                        />
                        {isTermsDefinitionsEnabled && touchedHints.annualLimit && renderTriggerHint(Strings.ANNUAL_LIMIT_HINT)}
                        {highlightAnnualLimitForbes && (
                            <div className="mt-4 flex items-center justify-between">
                                <span className="text-balance text-xs font-bold italic">{Strings.FORBES_ANNUAL_LIMIT}</span>
                                <ForbesLogo className="w-[65px]" data-testid="forbes-logo-annual-limit" />
                            </div>
                        )}
                    </FormField>
                )}

                {reimbursementRates && (
                    <FormField>
                        <DynamicOptionsField
                            variant={floatingVariant}
                            id="coverage-reimbursement-rate"
                            options={updatedReimbursementRates}
                            label={Strings.REIMBURSEMENT_RATE}
                            initialValue={reimbursementValue}
                            onValueChange={handleReimbursementChange}
                            showReimbursementTip={showReimbursementTip}
                            reimbursementVariant={showReimbursementSuggestion as ReimbursementTipVariant}
                        />
                        {isTermsDefinitionsEnabled && touchedHints.reimbursement && renderTriggerHint(Strings.REIMBURSEMENT_RATE_HINT)}
                        {showReimbursementTip && showReimbursementSuggestion === "affordance" && (
                            <>
                                <div className="flex w-full flex-row items-center justify-between gap-0">
                                    <FontAwesomeIcon icon={faBadgeDollar} className="mr-1 size-4 text-content-secondary" />
                                    <div className="mr-2 hidden text-xs font-medium text-content-secondary sm:block lg:hidden" data-testid={`more-affordable-inline-${view}`}>
                                        {Strings.MORE_AFFORDABLE}
                                    </div>
                                    <FontAwesomeIcon icon={faArrowLeft} className="mr-[-2px] size-4 text-content-inactive" />
                                    <div
                                        className="h-[2px] flex-1 bg-gradient-to-r from-content-inactive to-content-brand-b"
                                        data-testid={`reimbursement-affordance-gradient-${view}`}
                                    ></div>
                                    <FontAwesomeIcon icon={faArrowLeft} className="ml-[-2px] size-4 rotate-180 text-content-brand-b" />
                                    <FontAwesomeIcon icon={faShieldCheck} className="ml-2 size-4 text-content-brand-b" />
                                    <div className="ml-2 hidden text-xs font-medium text-content-secondary sm:block lg:hidden" data-testid={`most-cashback-inline-${view}`}>
                                        {Strings.MORE_CASHBACK}
                                    </div>
                                </div>
                                <div className="mt-1 flex justify-between text-xs font-medium sm:hidden lg:flex">
                                    <div className="text-content-secondary" data-testid={`more-affordable-stacked-${view}`}>
                                        {Strings.MORE_AFFORDABLE}
                                    </div>
                                    <div className="text-content-brand-b" data-testid={`most-cashback-stacked-${view}`}>
                                        {Strings.MORE_CASHBACK}
                                    </div>
                                </div>
                            </>
                        )}
                    </FormField>
                )}

                {deductibleOptions && (
                    <FormField>
                        <DynamicOptionsField
                            variant={floatingVariant}
                            id="coverage-annual-deductible"
                            label={Strings.ANNUAL_DEDUCTIBLE}
                            options={deductibleOptions}
                            initialValue={annualDeductibleValue}
                            onValueChange={handleDeductibleChange}
                        />
                        {isTermsDefinitionsEnabled && touchedHints.deductible && renderTriggerHint(Strings.ANNUAL_DEDUCTIBLE_HINT)}
                    </FormField>
                )}
            </div>
        </div>
    );
};
